import { AxiosError } from "axios";
import { IS_DEVELOPMENT } from "../constants/constants";

/**
 * Get a user-appropriate error message based on the current environment
 * @param error - The error object (AxiosError, Error, or unknown)
 * @returns A user-friendly error message string
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const isDev = IS_DEVELOPMENT;

    if (isDev) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || error.message;
      const endpoint = error.config?.url || "unknown endpoint";

      switch (status) {
        case 400:
          return `Bad Request (${status}): ${errorMessage} - Endpoint: ${endpoint}`;
        case 401:
          return `Unauthorized (${status}): ${errorMessage} - Check authentication token`;
        case 403:
          return `Forbidden (${status}): ${errorMessage} - Insufficient permissions for ${endpoint}`;
        case 404:
          return `Not Found (${status}): ${errorMessage} - Resource not found at ${endpoint}`;
        case 422:
          return `Validation Error (${status}): ${errorMessage} - Check request data`;
        case 500:
          return `Server Error (${status}): ${errorMessage} - Internal server error at ${endpoint}`;
        case 502:
          return `Bad Gateway (${status}): ${errorMessage} - Server is temporarily unavailable`;
        case 503:
          return `Service Unavailable (${status}): ${errorMessage} - Server is temporarily down`;
        case 504:
          return `Gateway Timeout (${status}): ${errorMessage} - Request took too long`;
        default:
          return `Request Error (${status || "unknown"}): ${errorMessage} - ${endpoint}`;
      }
    }

    switch (status) {
      case 400:
        return "Bad Request - Please refresh and try again.";
      case 401:
        return "User access issue. Please reach out to a system admin.";
      case 403:
        return "User access issue. Please reach out to a system admin.";
      default:
        return "System issues. Please refresh the TCP page. If the issue persists, submit a ticket.";
    }
  }

  if (error instanceof Error) {
    const isDev = IS_DEVELOPMENT;
    return isDev
      ? `Error: ${error.message} - ${error.stack?.split("\n")[1] || "Stack not available"}`
      : "System issues. Please refresh the TCP page. If the issue persists, submit a ticket.";
  }

  const isDev = IS_DEVELOPMENT;
  return isDev
    ? `Unknown error: ${JSON.stringify(error)}`
    : "System issues. Please refresh the TCP page. If the issue persists, submit a ticket.";
};

/**
 * Log detailed error information in development mode only
 * @param error - The error to log
 * @param context - Optional context string to help identify where the error occurred
 */
export const logErrorInDev = (error: unknown, context?: string) => {
  if (IS_DEVELOPMENT) {
    console.group(`🚨 Error ${context ? `in ${context}` : ""}`);
    console.error("Error details:", error);
    if (error instanceof AxiosError) {
      console.log("Request config:", error.config);
      console.log("Response data:", error.response?.data);
      console.log("Status:", error.response?.status);
      console.log("Headers:", error.response?.headers);
    }
    console.groupEnd();
  }
};
